import React, { ReactNode } from 'react';
import {
  AutoRelaySwitchScheduleEventDeviceConfig,
  DateOverMqttScheduleEventCommandConfig,
  RelaySwitchScheduleEventDeviceConfig,
  ScheduledEventRelaySwitchState,
  ScheduledEventDto,
  SingleRelaySwitchScheduleEventDeviceConfig,
  TasmotaPowerDeviceConfig,
  ScheduledRelayCommandConfig,
  ModbusScheduleEventConfigType,
} from '@thingslog/repositories';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const ScheduledEventSummary = ({ data }: ScheduledEventSummaryProps): JSX.Element => {
  const { t } = useTranslation();
  const renderAutoRelaySwitch = (data: AutoRelaySwitchScheduleEventDeviceConfig): ReactNode => {
    return (
      <>
        <div>{t('scheduled_task_event_form_required_state') + ':'}</div>
        <div>
          <Chip label={data.requiredState} size="small" />
        </div>
      </>
    );
  };

  const renderRelaySwitch = (data: RelaySwitchScheduleEventDeviceConfig): ReactNode => {
    return (
      <>
        <div>{t('scheduled_task_event_form_required_states')}</div>
        <div className="flex flex-wrap">
          {data.requiredStates.map((rs: ScheduledEventRelaySwitchState, index: number) => (
            <Chip key={`rsd-${index}`} label={rs} size="small" />
          ))}
        </div>
      </>
    );
  };

  const renderSingleRelaySwitch = (data: SingleRelaySwitchScheduleEventDeviceConfig): ReactNode => {
    return (
      <>
        <div>{t('sensor_index') + ':'}</div>
        <div>{data.sensorIndex}</div>
        <div>{t('scheduled_task_event_form_required_state') + ':'}</div>
        <div>
          <Chip label={data.requiredState} size="small" />
        </div>
      </>
    );
  };

  const renderSendDateOverMqtt = (data: DateOverMqttScheduleEventCommandConfig): ReactNode => {
    return null;
  };

  const renderModbus = (data: ModbusScheduleEventConfigType): ReactNode => {
    const renderSpecificFields = (): ReactNode => {
      switch (data['@type']) {
        case 'MODBUS_READ_COIL':
          return <></>;
        case 'MODBUS_READ_REGISTER':
          return <></>;
        case 'MODBUS_WRITE_COIL':
          return (
            <>
              <div>{t('scheduled_task_event_form_relay_state')}</div>
              <div>{data.relayState}</div>
            </>
          );
      }
    };

    return (
      <>
        <div>{t('sensor_index') + ':'}</div>
        <div>{data.sensorIndex}</div>
        <div>{t('slave_device_number') + ':'}</div>
        <div>{data.slaveDeviceNumber}</div>
        {renderSpecificFields()}
      </>
    );
  };

  const renderTasmotaPower = (data: TasmotaPowerDeviceConfig): ReactNode => {
    return (
      <>
        <div>{t('sensor_index') + ':'}</div>
        <div>{data.sensorIndex}</div>
        <div>{t('scheduled_task_event_form_required_state') + ':'}</div>
        <div>
          <Chip label={data.requiredState} size="small" />
        </div>
      </>
    );
  };

  const renderScheduledRelayCommand = (data: ScheduledRelayCommandConfig): ReactNode => {
    return (
      <>
        <div>{t('relay_index') + ':'}</div>
        <div>{data.relayIndex}</div>
        <div>{t('scheduled_task_event_form_required_state') + ':'}</div>
        <div>
          <Chip label={data.requiredState} size="small" />
        </div>
        <div>{t('scheduled_task_event_form_hour') + ':'}</div>
        <div>{data.hour}</div>
        <div>{t('scheduled_task_event_form_minute') + ':'}</div>
        <div>{data.minutes}</div>
      </>
    );
  };

  return (
    <section className="grid max-h-[70vh] max-w-[28rem] grid-cols-[auto_1fr] gap-1 gap-x-3 overflow-auto text-sm">
      <div>{`${t('task_scheduler_form_name')}:`}</div>
      <div>{data.eventName}</div>

      <div>{`${t('scheduled_task_event_form_start_date')}:`}</div>
      <div>{data.startDate}</div>

      <div>{`${t('scheduled_task_event_form_end_date')}:`}</div>
      <div>{data.endDate}</div>

      <div>{`${t('scheduled_task_event_form_execution_time')}:`}</div>
      <div>{data.executionTime}</div>

      <div>{`${t('scheduled_task_event_form_last_execution')}:`}</div>
      <div>{data.lastExecutionDate}</div>

      <div>{`${t('scheduled_task_event_form_event_type')}:`}</div>
      <div>{data.scheduleEventDeviceConfig['@type']}</div>

      {data.scheduleEventDeviceConfig['@type'] === 'AUTO_RELAY_SWITCH' &&
        renderAutoRelaySwitch(data.scheduleEventDeviceConfig)}
      {data.scheduleEventDeviceConfig['@type'] === 'RELAY_SWITCH' &&
        renderRelaySwitch(data.scheduleEventDeviceConfig)}
      {data.scheduleEventDeviceConfig['@type'] === 'SINGLE_RELAY_SWITCH' &&
        renderSingleRelaySwitch(data.scheduleEventDeviceConfig)}
      {data.scheduleEventDeviceConfig['@type'] === 'SEND_DATE_OVER_MQTT' &&
        renderSendDateOverMqtt(data.scheduleEventDeviceConfig)}
      {(data.scheduleEventDeviceConfig['@type'] === 'MODBUS_READ_REGISTER' ||
        data.scheduleEventDeviceConfig['@type'] === 'MODBUS_WRITE_COIL' ||
        data.scheduleEventDeviceConfig['@type'] === 'MODBUS_READ_COIL') &&
        renderModbus(data.scheduleEventDeviceConfig)}
      {data.scheduleEventDeviceConfig['@type'] === 'TASMOTA_POWER' &&
        renderTasmotaPower(data.scheduleEventDeviceConfig)}
      {data.scheduleEventDeviceConfig['@type'] === 'SCHEDULED_RELAY_COMMAND' &&
        renderScheduledRelayCommand(data.scheduleEventDeviceConfig)}

      <div className="col-span-2">{`${t('devices')}:`}</div>
      <div className="col-span-2 flex flex-wrap gap-1.5">
        {data.scheduleEventDeviceConfig.deviceNumbers.map((dn: string) => (
          <Chip key={dn} variant="filled" label={dn} size="small" />
        ))}
      </div>
    </section>
  );
};

interface ScheduledEventSummaryProps {
  data: ScheduledEventDto;
}
